import React, { useState } from 'react';
import {
  View, ScrollView, TouchableOpacity, StyleSheet, Alert, RefreshControl,
  TextInput, ActivityIndicator, Text as RNText,
} from 'react-native';
import { Text } from '@/components';
import { Image } from 'expo-image';
import { ArrowLeft, Users, Backpack, Mail, X, Dog, LogOut, Trash2 } from 'lucide-react-native';
import { useAuth } from '@/context/AuthContext';
import {
  useGroup, useGroupInvites, useGroupTrails,
  useCreateInvite, useRevokeInvite, useDeleteGroup, useRemoveMember,
} from '@/lib/queries/groups';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import LottieLoader from '@/components/LottieLoader';
import TrailCard from '@/components/TrailCard';
import { logEvent } from '@/lib/analytics';
import GroupChat from './[id]/chat';
import { useInviteSuggestions, useAcceptInvite } from '@/lib/queries/groups';
import * as Haptics from 'expo-haptics';

export default function GroupDetailScreen() {
  const { t } = useTranslation();
  const { id, view } = useLocalSearchParams<{ id: string; view?: string }>();
  const { session, user } = useAuth();
  const token = session?.accessToken;
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const groupQuery = useGroup(token, id);
  const invitesQuery = useGroupInvites(token, id);
  const trailsQuery = useGroupTrails(token, id);
  const group = groupQuery.data;

  const createInvite = useCreateInvite(token);
  const revokeInvite = useRevokeInvite(token);
  const deleteGroup = useDeleteGroup(token);
  const removeMember = useRemoveMember(token);

  const [email, setEmail] = useState('');
  const [activeView, setActiveView] = useState<'info' | 'chat'>(
    view === 'chat' ? 'chat' : 'info');
  const isMember = (group?.members ?? []).some((m) => m.userId === user?.id);
  const pendingInviteCode = (group as any)?.pendingInviteCode ?? null;
  const acceptInviteMutation = useAcceptInvite(token);
  const [accepting, setAccepting] = useState(false);

  const handleAcceptInvite = async () => {
    if (!group || !pendingInviteCode) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setAccepting(true);
    try {
      await acceptInviteMutation.mutateAsync(pendingInviteCode);
      logEvent('group_joined', { via: 'push' });
      await groupQuery.refetch();
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('groups.inviteFailed'));
    } finally {
      setAccepting(false);
    }
  };
  const suggestions = useInviteSuggestions(token, id, email);

  const isOwner = !!group && group.createdBy === user?.id;

  const handleInvite = async (targetOverride?: string) => {
    const target = (targetOverride ?? email).trim();
    if (!target) return;
    try {
      await createInvite.mutateAsync({ groupId: id, email: target });
      logEvent('group_invite_sent', { group_id: String(id) });
      setEmail('');
      Alert.alert(t('groups.inviteSentTitle'), t('groups.inviteSentBody'));
    } catch (e: any) {
      const msg = /no one with that tail tag/i.test(e?.message || '') ? t('groups.tailTagNotFound') : e?.message || t('groups.inviteFailed');
      Alert.alert(t('common.error'), msg);
    }
  };

  const handleRevoke = (inviteId: string) => {
    Alert.alert(t('groups.revoke'), t('groups.revokeConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('common.remove'), style: 'destructive', onPress: () => revokeInvite.mutate({ groupId: id, inviteId }) },
    ]);
  };

  const handleLeaveOrDelete = () => {
    if (!group) return;
    if (isOwner) {
      Alert.alert(t('groups.delete'), t('groups.deleteConfirm', { name: group.name }), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('common.delete'), style: 'destructive', onPress: async () => {
          try { await deleteGroup.mutateAsync(id); router.replace('/(tabs)/groups' as any); } catch (e: any) { Alert.alert(t('common.error'), e?.message); }
        } },
      ]);
    } else {
      Alert.alert(t('groups.leave'), t('groups.leaveConfirm', { name: group.name }), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('common.remove'), style: 'destructive', onPress: async () => {
          try { await removeMember.mutateAsync({ groupId: id, userId: user!.id }); router.replace('/(tabs)/groups' as any); } catch (e: any) { Alert.alert(t('common.error'), e?.message); }
        } },
      ]);
    }
  };

  if (groupQuery.isLoading) {
    return <View style={styles.center}><LottieLoader size={120} /></View>;
  }
  if (!group) {
    return <View style={[styles.center, { paddingTop: insets.top }]}><Text style={styles.empty}>{t('groups.notFound')}</Text></View>;
  }

  const members = group.members ?? [];
  const invites = (invitesQuery.data ?? []).filter((i) => !i.acceptedAt);
  const trails = trailsQuery.data ?? [];

  // Invited via push but not yet a member: show an accept card instead of the
  // group (the members-only endpoints 403/404 until they accept).
  if (group && !isMember && pendingInviteCode && !accepting) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
            <ArrowLeft size={26} color={theme.accentPrimary} />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={styles.title}>{group.name}</Text>
          </View>
          <View style={{ width: 26 }} />
        </View>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32 }}>
          {group.type === 'family' ? <Users size={48} color={theme.accentPrimary} /> : <Backpack size={48} color={theme.accentPrimary} />}
          <Text style={[styles.title, { marginTop: 16, textAlign: 'center' }]}>
            {t('groups.invitedTo', { name: group.name })}
          </Text>
          <Text style={[styles.empty, { marginTop: 8, textAlign: 'center' }]}>
            {t('groups.invitedSubtitle')}
          </Text>
          <TouchableOpacity
            style={{ marginTop: 24, backgroundColor: theme.accentPrimary, borderRadius: 12, paddingVertical: 12, paddingHorizontal: 32 }}
            onPress={handleAcceptInvite}
          >
            <Text style={{ color: theme.backgroundPrimary, fontWeight: '700', fontSize: 15 }}>
              {t('groups.acceptInvite')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Chat active: render the chat OUTSIDE the members ScrollView — a VirtualizedList
  // (chat FlatList) nested inside a ScrollVist of the same orientation breaks windowing.
  if (group && isMember && activeView === 'chat') {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
            <ArrowLeft size={26} color={theme.accentPrimary} />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            {group.type === 'family' ? <Users size={22} color={theme.accentPrimary} /> : <Backpack size={22} color={theme.accentPrimary} />}
            <Text style={styles.title}>{group.name}</Text>
          </View>
          <TouchableOpacity onPress={handleLeaveOrDelete} hitSlop={12}>
            {isOwner ? <Trash2 size={22} color={theme.textMuted} /> : <LogOut size={22} color={theme.textMuted} />}
          </TouchableOpacity>
        </View>

        <View style={styles.entryRow}>
          <TouchableOpacity
            style={styles.entryTab}
            onPress={() => setActiveView('info')}
          >
            <Text style={styles.entryTabText}>{t('groups.info')}</Text>
          </TouchableOpacity>
          <View style={[styles.entryTab, { backgroundColor: theme.accentPrimary }]}
          >
            <Text style={styles.entryTabTextActive}>{t('groups.planAWalk')}</Text>
          </View>
        </View>

        <View style={{ flex: 1 }}>
          <GroupChat embedded />
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={{ paddingTop: insets.top + 12, paddingBottom: insets.bottom + 40 }}
      refreshControl={<RefreshControl refreshing={groupQuery.isFetching} onRefresh={() => groupQuery.refetch()} tintColor={theme.accentPrimary} />}
    >
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}><ArrowLeft size={26} color={theme.accentPrimary} /></TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          {group.type === 'family' ? <Users size={22} color={theme.accentPrimary} /> : <Backpack size={22} color={theme.accentPrimary} />}
          <Text style={styles.title}>{group.name}</Text>
        </View>
        <TouchableOpacity onPress={handleLeaveOrDelete} hitSlop={12}>
          {isOwner ? <Trash2 size={22} color={theme.textMuted} /> : <LogOut size={22} color={theme.textMuted} />}
        </TouchableOpacity>
      </View>

      {isMember && (
        <View style={styles.entryRow}>
          <TouchableOpacity
            style={[styles.entryTab, activeView === 'info' && styles.entryTabActive]}
            onPress={() => setActiveView('info')}
          >
            <Text style={[styles.entryTabText, activeView === 'info' && styles.entryTabTextActive]}>{t('groups.info')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.entryTab, activeView === 'chat' && styles.entryTabActive]}
            onPress={() => {
              if (activeView !== 'chat') { setActiveView('chat'); logEvent('group_chat_opened', { group_id: String(id) }); }
            }}
          >
            <Text style={[styles.entryTabText, activeView === 'chat' && styles.entryTabTextActive]}>{t('groups.planAWalk')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Members */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('groups.members')} ({members.length})</Text>
        {members.map((m) => (
          <View key={m.userId} style={styles.memberRow}>
            {m.image ? (
              <Image source={m.image} style={styles.avatar} contentFit="cover" cachePolicy="memory-disk" />
            ) : (
              <View style={[styles.avatar, styles.avatarPlaceholder]}><Dog size={16} color={theme.textMuted} /></View>
            )}
            <Text style={styles.memberName}>{m.name || m.userId}</Text>
            {m.role === 'owner' && <Text style={styles.ownerBadge}>{t('groups.owner')}</Text>}
            {isOwner && m.role !== 'owner' && (
              <TouchableOpacity onPress={() => removeMember.mutate({ groupId: id, userId: m.userId })}>
                <X size={18} color={theme.textMuted} />
              </TouchableOpacity>
            )}
          </View>
        ))}
      </View>

      {/* Invite by email (owner only) */}
      {isOwner && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('groups.inviteByEmail')}</Text>
          <View style={styles.inviteRow}>
            <Mail size={18} color={theme.textMuted} style={{ marginLeft: 4 }} />
            <TextInput
              style={styles.inviteInput}
              value={email}
              onChangeText={setEmail}
              placeholder={t('groups.invitePlaceholder')}
              placeholderTextColor={theme.textMuted}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <TouchableOpacity style={styles.inviteBtn} onPress={() => handleInvite()} disabled={createInvite.isPending || !email.trim()}>
              {createInvite.isPending ? <ActivityIndicator color={theme.backgroundPrimary} /> : <Text style={styles.inviteBtnText}>{t('groups.invite')}</Text>}
            </TouchableOpacity>
          </View>

          {(suggestions.data ?? []).length > 0 && (
            <View style={styles.suggestionList}>
              {(suggestions.data ?? []).map((u) => (
                <TouchableOpacity
                  key={u.id}
                  style={styles.suggestionRow}
                  onPress={() => { setEmail(''); handleInvite(u.email); }}
                >
                  {u.image ? (
                    <Image source={u.image} style={styles.suggestionAvatar} contentFit="cover" cachePolicy="memory-disk" />
                  ) : (
                    <View style={[styles.suggestionAvatar, styles.suggestionAvatarFallback]}>
                      <Dog size={14} color={theme.textMuted} />
                    </View>
                  )}
                  <Text style={styles.suggestionName}>{u.name || u.email}</Text>
                  <Text style={styles.suggestionTag}>{`@${u.tail_tag}`}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {invites.length > 0 && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.sectionTitle}>{t('groups.pendingInvites')} ({invites.length})</Text>
              {invites.map((inv) => (
                <View key={inv.id} style={styles.inviteRow}>
                  <Text style={styles.inviteEmail}>{inv.email}</Text>
                  <TouchableOpacity onPress={() => handleRevoke(inv.id)}>
                    <Text style={styles.revokeText}>{t('groups.revoke')}</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Trails shared to this group */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('groups.sharedTrails')} ({trails.length})</Text>
        {trailsQuery.isLoading ? (
          <ActivityIndicator color={theme.accentPrimary} />
        ) : trails.length === 0 ? (
          <Text style={styles.empty}>{t('groups.noSharedTrails')}</Text>
        ) : (
          trails.map((tr) => (
            <TrailCard key={tr.id} trail={tr} onPress={(tid) => router.push(`/trail/${tid}`)} />
          ))
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.backgroundPrimary },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingBottom: 12 },
  title: { ...Typography.body(theme.textPrimary), fontSize: 18, fontWeight: '700', marginTop: 4 },
  section: { padding: 16, borderTopWidth: 1, borderTopColor: theme.borderSubtle },
  sectionTitle: { ...Typography.label(theme.textMuted), marginBottom: 10 },
  memberRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8 },
  avatar: { width: 36, height: 36, borderRadius: 18 },
  avatarPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: theme.backgroundSecondaryVarient },
  memberName: { ...Typography.body(theme.textPrimary), flex: 1, fontSize: 15 },
  ownerBadge: { ...Typography.caption(theme.accentPrimary), fontSize: 11, borderWidth: 1, borderColor: theme.accentPrimary, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, marginRight: 8 },
  inviteRow: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: theme.backgroundSecondary, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 6 },
  inviteInput: { flex: 1, color: theme.textPrimary, fontSize: 15, paddingVertical: 8 },
  inviteBtn: { backgroundColor: theme.accentPrimary, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10 },
  inviteBtnText: { color: theme.backgroundPrimary, fontWeight: '700', fontSize: 14 },
  inviteEmail: { ...Typography.body(theme.textPrimary), flex: 1, fontSize: 14 },
  revokeText: { color: '#ff6b6b', fontSize: 13, fontWeight: '600' },
  empty: { ...Typography.body(theme.textMuted), fontSize: 14 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.backgroundPrimary },
  entryRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingBottom: 12 },
  entryTab: { flex: 1, alignItems: 'center', paddingVertical: 10, borderRadius: 12, backgroundColor: theme.backgroundSecondary },
  entryTabActive: { backgroundColor: theme.accentPrimary },
  entryTabText: { color: theme.textMuted, fontWeight: '600', fontSize: 14 },
  entryTabTextActive: { color: theme.backgroundPrimary },
  chatWrap: { minHeight: 500 },
  suggestionList: { marginTop: 6, marginBottom: 18, borderRadius: 12, backgroundColor: theme.backgroundSecondary, overflow: 'hidden' },
  suggestionRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 10, borderTopWidth: 1, borderTopColor: theme.borderSubtle },
  suggestionAvatar: { width: 30, height: 30, borderRadius: 15 },
  suggestionAvatarFallback: { alignItems: 'center', justifyContent: 'center', backgroundColor: theme.backgroundSecondary },
  suggestionName: { color: theme.textPrimary, fontSize: 14, flex: 1, marginLeft: 10 },
  suggestionTag: { color: theme.accentPrimary, fontSize: 12, marginLeft: 8 },
});